import React, { useCallback, useEffect, useRef } from 'react';

import { Box, Icon, VStack } from '@rbilabs/universal-components';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { useRouter } from 'expo-router';
import { IPaymentMethod } from 'features/payment/types';
import { mapPaymentMethodToLoyaltyPaymentMethods } from 'features/payment/utils';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { ICartEntry } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants, ActionLink } from 'components/action-button';
import { CartItemExperiment } from 'components/cart-item-experiment';
import { useCart } from 'hooks/use-cart';
import Totals from 'pages/cart/your-cart/totals';
import { useCartContext } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useRemoveExpiredOfferFromCart } from 'state/loyalty/offers/hooks/use-remove-expired-offer-from-cart';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';

import { Upsell } from '../upsell';

import DonationContainer from './donation-container';
import FoodwareContainer from './foodware-container';
import PriceMismatchInternalOnly from './price-mismatch-internal-only';
import { ActionButtonsContainer, ActionLinkContainer } from './styled';
import YourCartHeading from './your-cart-heading';

interface IYourCartProps {
  checkoutPaymentMethod?: IPaymentMethod | null;
  readOnly?: boolean;
  showAddItemsBtn?: boolean;
  showAddExtrasBtn?: boolean;
  showCartPromoCodes?: boolean;
}

const YourCart: React.FC<React.PropsWithChildren<IYourCartProps>> = ({
  checkoutPaymentMethod,
  readOnly = false,
  showAddItemsBtn = true,
  showAddExtrasBtn = true,
  showCartPromoCodes = true,
}) => {
  const { cartEntries, calculateCartTotalWithDiscount, addItemToCart } = useCart();
  const isDonationsEnabled = useFlag(LaunchDarklyFlag.ENABLE_FOUNDATION_DONATIONS);
  const router = useRouter();

  const {
    orderStatus,
    pricedAndAvailable = true,
    priceOrderSuccess,
    priceMutationError,
    serverOrder,
    serviceMode,
    shouldSkipPriceOrder,
    a11yMessage,
    announceA11yCartMessage,
  } = useCartContext();
  const { formatMessage } = useIntl();
  const { loyaltyUser } = useLoyaltyUser();
  const { evaluateLoyaltyUserIncentives } = useLoyaltyContext();
  const showActionButtons = showAddItemsBtn || showAddExtrasBtn;
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const dispatch = useAppDispatch();

  const appliedRewardsCount = useRef(0);
  const cartEntriesCount = useRef(0);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount
    ? calculateCartTotalWithDiscount()
    : {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  useEffect(() => {
    // Resetting offer feedback on mount to avoid carrying over feedback from offer list
    dispatch(actions.loyalty.resetOfferFeedbackMap());
  }, [dispatch]);

  useEffect(() => {
    /**
     * If an unauthenticated user adds items to their cart and then login,
     * they'll likely be part of the Cart v2 experiment.
     * In this case, we'll need to adjust cart entries with quantities greater than 1 to fit the new Cart v2 flow.
     */

    const cartEntriesToBeExpanded = cartEntries.filter(item => item.quantity && item.quantity > 1);

    if (cartEntriesToBeExpanded.length) {
      const expandedCartEntries: ICartEntry[] = [];

      cartEntriesToBeExpanded.forEach(item => {
        const expandedCartEntry = [...Array(item.quantity)].map(() => ({
          ...item,
          quantity: 1,
          cartId: uuidv4(),
        }));
        expandedCartEntries.push(...expandedCartEntry);
      });

      expandedCartEntries.forEach(item => {
        dispatch(actions.ordering.addCartEntry(item));
      });

      // Remove the original expanded cart entries to improve the order of the cart items.
      dispatch(
        actions.ordering.removeCartEntries(
          new Set(cartEntriesToBeExpanded.map(item => item.cartId))
        )
      );
    }
  }, [addItemToCart, cartEntries, dispatch]);

  const runIncentivesEvaluation = useCallback(() => {
    if (loyaltyUser?.id && appliedLoyaltyOffers.length) {
      const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

      evaluateLoyaltyUserIncentives(
        loyaltyUser?.id,
        appliedLoyaltyOffers,
        cartEntries,
        appliedLoyaltyRewards,
        positiveCartTotal,
        paymentMethod
      );
    }
  }, [
    loyaltyUser,
    appliedLoyaltyOffers,
    checkoutPaymentMethod,
    evaluateLoyaltyUserIncentives,
    cartEntries,
    appliedLoyaltyRewards,
    positiveCartTotal,
  ]);

  useEffect(() => {
    if (priceOrderSuccess && !priceMutationError) {
      runIncentivesEvaluation();
    }
  }, [priceMutationError, priceOrderSuccess, runIncentivesEvaluation]);

  useEffect(() => {
    if (pricedAndAvailable) {
      const validCartEntriesCount =
        // Donation and Extras entries should not be taken into account for querying offers
        cartEntries?.filter(({ isExtra, isDonation }: ICartEntry) => !isDonation && !isExtra)
          .length ?? 0;
      const appliedRewardsLength = Object.keys(appliedLoyaltyRewards).length;

      appliedRewardsCount.current = appliedRewardsLength;
      cartEntriesCount.current = validCartEntriesCount;
    }
  }, [appliedLoyaltyRewards, cartEntries, pricedAndAvailable]);

  useRemoveExpiredOfferFromCart();

  return (
    <Box>
      <YourCartHeading />

      <Box accessibilityLabel="cart-section-heading-label">
        <View
          accessible={false}
          accessibilityLabel={a11yMessage}
          accessibilityLiveRegion="polite"
          style={{
            width: 0,
            height: 0,
            opacity: 0,
          }}
          testID="cart-accessibility-message-announcer"
        />
        <VStack testID="cart-items">
          {cartEntries.map((item: ICartEntry) => (
            <CartItemExperiment
              item={item}
              key={item.cartId || item._id}
              readOnly={readOnly}
              rewardApplied={appliedLoyaltyRewards[item.cartId]?.timesApplied > 0}
              showReadOnlyQuantity
              onAnnounceA11yMessage={announceA11yCartMessage}
            />
          ))}
        </VStack>

        {showActionButtons && (
          <ActionButtonsContainer>
            {showAddExtrasBtn && enableAddExtras && (
              <ActionLinkContainer>
                <ActionLink
                  to={routes.cartAddExtras}
                  startIcon={<Icon variant="add" {...hiddenAccessibilityPlatformProps} />}
                  variant={ActionButtonVariants.OUTLINE}
                >
                  {formatMessage({ id: 'addCondiments' })}
                </ActionLink>
              </ActionLinkContainer>
            )}

            {showAddItemsBtn && (
              <ActionLinkContainer>
                <ActionButton
                  onPress={() => router.navigate(routes.menu)}
                  startIcon={<Icon variant="add" {...hiddenAccessibilityPlatformProps} />}
                  variant={ActionButtonVariants.OUTLINE}
                  eventAttributes={{
                    Name: formatMessage({ id: 'addItemsLowerCase' }),
                  }}
                >
                  {formatMessage({ id: 'addItems' })}
                </ActionButton>
              </ActionLinkContainer>
            )}
          </ActionButtonsContainer>
        )}
        <Upsell isInCart />
        <FoodwareContainer />

        {isDonationsEnabled && <DonationContainer />}

        <PriceMismatchInternalOnly />

        <Totals
          // @ts-expect-error TS(2322) FIXME: Type '{ orderStatus: OrderStatus | null; serverOrd... Remove this comment to see the full error message
          orderStatus={orderStatus}
          serverOrder={serverOrder}
          serviceMode={serviceMode}
          shouldSkipPriceOrder={shouldSkipPriceOrder}
        />
      </Box>
    </Box>
  );
};

export default React.memo(YourCart);
